import { Button } from "@/components/ui/button";
import { Award, Heart, Users, Target } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { AccessibilityButton } from "@/components/AccessibilityButton";
import { Timeline } from "@/components/Timeline";
import { TeamGallery } from "@/components/TeamGallery";
import { useNossaHistoria } from "@/hooks/useNossaHistoria";
import { Skeleton } from "@/components/ui/skeleton";

export default function NossaHistoria() {
  const { content, contentLoading } = useNossaHistoria();

  const getContent = (section: string, key: string, defaultValue: string = '') => {
    const item = content?.find(c => c.section === section && c.content_key === key);
    return item?.content_value?.text || defaultValue;
  };

  const getPrincipleContent = (key: string) => {
    const item = content?.find(c => c.section === 'principle' && c.content_key === key);
    return item?.content_value || {};
  };

  const heroTitle = getContent('hero', 'title', 'Nossa História');
  const heroSubtitle = getContent('hero', 'subtitle', 'Conheça a jornada da Dra Brenda Ramos e sua missão de transformar vidas através da nutrição');
  const storyTitle = getContent('story', 'title', 'Uma Jornada de Dedicação e Propósito');
  const storyP1 = getContent('story', 'paragraph1');
  const storyP2 = getContent('story', 'paragraph2');
  const crn = getContent('story', 'crn', '3/63492');

  const humanization = getPrincipleContent('humanization');
  const sustainability = getPrincipleContent('sustainability');
  const evidence = getPrincipleContent('evidence');

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-24 bg-gradient-to-r from-[#f4fbf7] to-white py-12 sm:py-16">
        <div className="container mx-auto mobile-container">
          <div className="text-center mb-12">
            {contentLoading ? (
              <>
                <Skeleton className="h-12 w-2/3 mx-auto mb-6" />
                <Skeleton className="h-6 w-3/4 mx-auto" />
              </>
            ) : (
              <>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-primary mb-6">{heroTitle}</h1>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {heroSubtitle}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Story Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {contentLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-10 w-3/4" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : (
              <div>
                <h2 className="text-3xl font-bold text-brand-primary mb-6">
                  {storyTitle}
                </h2>
                {storyP1 && (
                  <p className="text-lg font-text text-gray-600 mb-6 leading-relaxed">
                    {storyP1}
                  </p>
                )}
                {storyP2 && (
                  <p className="text-lg font-text text-gray-600 mb-6 leading-relaxed">
                    {storyP2}
                  </p>
                )}
                <div className="bg-brand-primary/5 border border-brand-primary/10 p-6 rounded-2xl">
                  <div className="flex items-center mb-2">
                    <div className="w-10 h-10 bg-brand-primary/10 rounded-xl flex items-center justify-center mr-3">
                      <Award className="w-5 h-5 text-brand-primary" />
                    </div>
                    <span className="font-bold text-brand-primary">CRN: {crn}</span>
                  </div>
                  <p className="text-brand-primary/70 text-sm font-text">
                    Nutricionista registrada no Conselho Regional de Nutricionistas
                  </p>
                </div>
              </div>
            )}
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-brand-primary/5 rounded-3xl" />
                <div className="relative w-72 h-72 bg-brand-primary rounded-3xl flex items-center justify-center text-white text-7xl font-bold shadow-xl shadow-brand-primary/20">
                  BR
                </div>
              </div>
            </div>
          </div>

          {/* Princípios */}
          {contentLoading ? (
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-64 w-full" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              <div className="bg-white rounded-2xl border border-brand-primary/10 p-6 text-center hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 bg-brand-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-7 h-7 text-brand-primary" />
                </div>
                <h3 className="text-lg font-bold mb-3 text-gray-800 font-accent">
                  {humanization.title || 'Humanização'}
                </h3>
                <p className="font-text text-gray-500 text-sm leading-relaxed">
                  {humanization.description || 'Acreditamos que o cuidado nutricional deve ser humano.'}
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-brand-secondary/10 p-6 text-center hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 bg-brand-secondary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Target className="w-7 h-7 text-brand-secondary" />
                </div>
                <h3 className="text-lg font-bold mb-3 text-gray-800 font-accent">
                  {sustainability.title || 'Sustentabilidade'}
                </h3>
                <p className="font-text text-gray-500 text-sm leading-relaxed">
                  {sustainability.description || 'Desenvolvemos estratégias nutricionais sustentáveis.'}
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-brand-accent/10 p-6 text-center hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 bg-brand-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-brand-accent" />
                </div>
                <h3 className="text-lg font-bold mb-3 text-gray-800 font-accent">
                  {evidence.title || 'Evidência Científica'}
                </h3>
                <p className="font-text text-gray-500 text-sm leading-relaxed">
                  {evidence.description || 'Baseamos nossa prática em evidências científicas.'}
                </p>
              </div>
            </div>
          )}

          {/* Timeline Section */}
          <div className="mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-primary mb-8 text-center">
              Nossa Trajetória
            </h2>
            <Timeline />
          </div>

          {/* Team Section */}
          <div className="mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-primary mb-4 text-center">
              Nossa Equipe
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 text-center mb-8 max-w-3xl mx-auto">
              Conheça os profissionais dedicados que tornam possível nosso trabalho de transformação através da nutrição
            </p>
            <TeamGallery />
          </div>

          {/* CTA Section */}
          <div className="text-center bg-brand-primary rounded-3xl py-12 px-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Pronto para Iniciar sua Jornada?
              </h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                Agende uma consulta e descubra como podemos transformar sua relação com a alimentação
              </p>
              <Button
                size="lg"
                className="bg-white text-brand-primary hover:bg-white/90 font-accent font-bold shadow-xl"
                onClick={() => window.open('https://wa.me/5511998874081', '_blank')}
              >
                Falar com a Doutora
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
      <AccessibilityButton />
    </div>
  );
}
